import { useEffect, useState } from 'react';
import { findPackage, PREPAID_CATALOGUE, walletView, type GetBillingOverviewResponse } from '@acs/shared';
import { Button, Checkbox, Icon, Input, SaveBar, Select, Skeleton, Switch, useToast } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { billingErrorDetails } from '../../shared/api/billing.js';
import { formatMoney } from './format.js';
import { billingError } from './billingText.js';
import { useQuoteTopup, useSaveAutoTopup, useStartCardSetup } from './prepaidHooks.js';

type Overview = GetBillingOverviewResponse;

/**
 * The monthly limit saved on the owner's behalf while the limit has no input
 * of its own (owner's decision, 14 Oct 2026). The server still requires one
 * (doc 42 §9), so this is a safety ceiling, never the reason a top-up stops:
 * this many purchases of the chosen package a month. A limit saved earlier is
 * kept when it covers at least one purchase.
 */
const AUTO_CAP_PURCHASES = 10;

/**
 * The owner's on/off choice from BEFORE subscribing (the switch is live in
 * the preview). Nothing can be saved then — no card, no subscription — so it
 * is kept for the tab, across the round trip to Checkout, and becomes the
 * form's starting point once subscribed. On unless they switched it off.
 */
const choiceKey = (businessId: string) => `tc.autoTopupChoice.${businessId}`;
function readChoice(businessId: string): boolean {
  try {
    return window.sessionStorage.getItem(choiceKey(businessId)) !== 'off';
  } catch {
    return true;
  }
}
function writeChoice(businessId: string, on: boolean): void {
  try {
    window.sessionStorage.setItem(choiceKey(businessId), on ? 'on' : 'off');
  } catch {
    /* storage unavailable — the choice lives in component state only */
  }
}

export type AutoTopupMode = 'onboarding' | 'settings';

/**
 * Automatic top-up — ONE widget for the wizard's Activate step and for
 * Settings › Billing (top-up widget brief, 14 Oct 2026; doc 42 §4.4, §9).
 *
 * The form state lives in this hook, outside the widget, because in the
 * wizard the step's own Continue saves it ("Save and continue"); in Settings
 * the widget shows the usual save bar. Never saved or charged silently: the
 * owner picks the package and threshold, sees the list price + VAT, and ticks
 * the consent for THIS business; the exact gross is quoted at save time.
 *
 * - `onboarding`: proposed ON for a business that never saved settings (or as
 *   the owner left the switch before paying); switching it off is saved too.
 * - `settings`: starts from what is saved.
 *
 * Inert (`ready: false`) until `active` (subscribed) and the overview loaded.
 */
export function useAutoTopup(
  business: BusinessDoc,
  ov: Overview | undefined,
  { active, mode, onDone = () => {} }: { active: boolean; mode: AutoTopupMode; onDone?: () => void },
) {
  const { t, tag } = useI18n();
  const { toast } = useToast();
  const save = useSaveAutoTopup(business.id);
  const quote = useQuoteTopup(business.id);
  const stored = ov?.autoTopup;
  const onboarding = mode === 'onboarding';
  const firstTime = !stored || stored.revision === 0;
  // Saved ON shows a status row (Edit opens the form again). Anything else —
  // nothing saved yet, or saved OFF — is the form, showing the owner's choice.
  const savedOn = !!stored?.enabled;
  // Saved ON under older terms (or a consent that is gone): the owner must
  // re-accept, so the form opens with the consent unticked.
  const termsStale = savedOn && !!ov && ov.autoConsent?.termsVersion !== ov.termsVersion;
  // Saved ON but the server holds purchases until the owner re-agrees to a
  // changed price (or tax): same as stale terms.
  const priceReview = savedOn && business.billingSummary.paymentState === 'price_review_required';
  const reconsent = termsStale || priceReview;
  const openForm = !savedOn || reconsent;

  // Before subscribing: the preview's switch (kept for the tab; wizard only).
  const [preference, setPreference] = useState(() => (onboarding ? readChoice(business.id) : true));

  // A saved threshold, else the package's own default (30/60/100/200 min).
  const startThreshold = (packageId: string) => {
    const saved = stored?.triggerSeconds ?? 0;
    const fallback = findPackage(PREPAID_CATALOGUE, packageId, ov?.currency ?? business.currency)?.defaultTriggerSeconds ?? 1800;
    return String(Math.round((saved > 0 ? saved : fallback) / 60));
  };

  const initial = () => {
    const packageId = stored?.packageId ?? ov?.packages[0]?.packageId ?? '';
    return {
      enabled: onboarding && firstTime ? readChoice(business.id) : !!stored?.enabled,
      packageId,
      thresholdMin: startThreshold(packageId),
      accepted: false,
    };
  };
  const [form, setForm] = useState(initial);
  // Saved settings show as a status row; the form opens again with Edit.
  const [editing, setEditing] = useState(openForm);
  const [thresholdTouched, setThresholdTouched] = useState(false);
  const [thresholdTyped, setThresholdTyped] = useState(false);
  // Every way back to a clean form goes through here, so none of them keeps
  // a half-typed threshold or a touched flag from an earlier edit.
  const reset = (edit: boolean) => {
    setForm(initial());
    setEditing(edit);
    setThresholdTouched(false);
    setThresholdTyped(false);
  };
  useEffect(() => {
    reset(openForm);
  }, [stored?.revision, ov?.termsVersion, ov?.autoConsent?.termsVersion, priceReview, business.id, !!ov]); // eslint-disable-line react-hooks/exhaustive-deps

  const pkg = ov?.packages.find((p) => p.packageId === form.packageId) ?? null;
  const price = pkg && pkg.unitAmountMinor !== null ? formatMoney(pkg.unitAmountMinor, pkg.currency, tag) : null;
  const thresholdSeconds = Math.round(Number(form.thresholdMin) * 60);
  const thresholdBad = !Number.isFinite(thresholdSeconds) || thresholdSeconds <= 0 || !pkg || thresholdSeconds >= pkg.seconds;
  const available = walletView(business.billingSummary, new Date()).availableSeconds;

  const blocked = !active || !ov || !editing || !form.enabled
    ? null
    : !ov?.card
      ? onboarding ? t('plan.auto.noCard') : t('prepaid.auto.needCard')
      : !price
        ? t('plan.auto.unavailable')
        : thresholdBad
          ? t('prepaid.auto.fixErrors')
          : !form.accepted
            ? reconsent ? t('plan.auto.reconsent') : t('plan.auto.needConsent')
            : null;

  // Saving "off" needs no consent and no price. In the wizard it is saved on
  // a first visit too, so the owner's "no" is recorded and the step does not
  // propose "on" again when they come back.
  const persist = async () => {
    if (!ov || !stored) return;
    if (!form.enabled) {
      await save.mutateAsync({
        enabled: false,
        packageId: form.packageId,
        triggerSeconds: thresholdBad ? Math.max(60, stored.triggerSeconds) : thresholdSeconds,
        monthlyGrossCapMinor: stored.monthlyGrossCapMinor,
        acceptedGrossPerPurchaseMinor: stored.maxGrossPerPurchaseMinor ?? 0,
        acceptedTermsVersion: null,
        expectedRevision: stored.revision,
      });
      toast({ title: t('prepaid.auto.saved'), tone: 'success' });
      return;
    }
    // The owner agreed to the list price + VAT; the exact gross is priced by
    // the server now, at the moment of saving, so it can never be stale.
    const q = await quote.mutateAsync(form.packageId);
    if (!q.taxComplete) throw Object.assign(new Error('tax'), { taxIncomplete: true });
    const cap =
      stored.monthlyGrossCapMinor !== null && stored.monthlyGrossCapMinor >= q.grossMinor
        ? stored.monthlyGrossCapMinor
        : q.grossMinor * AUTO_CAP_PURCHASES;
    const r = await save.mutateAsync({
      enabled: true,
      packageId: form.packageId,
      triggerSeconds: thresholdSeconds,
      monthlyGrossCapMinor: cap,
      acceptedGrossPerPurchaseMinor: q.grossMinor,
      acceptedTermsVersion: ov.termsVersion,
      expectedRevision: stored.revision,
    });
    toast({ title: r.topupQueued ? t('prepaid.auto.savedQueued') : t('prepaid.auto.saved'), tone: 'success' });
  };

  const busy = save.isPending || quote.isPending;
  const failed = (e: unknown) => {
    // Never carry a tick over to a different amount: the owner re-agrees.
    if (billingErrorDetails(e).code === 'price_changed') setForm((f) => ({ ...f, accepted: false }));
    if ((e as { taxIncomplete?: boolean }).taxIncomplete) {
      toast({ title: t('prepaid.error.tax_location_required'), tone: 'danger' });
      return;
    }
    toast({ title: billingErrorDetails(e).code === 'price_changed' ? t('prepaid.auto.err.per_purchase_below_price') : billingError(t, e), tone: 'danger' });
  };

  // Wizard: something to save — turning it on (or changing it), turning it
  // off, or the first answer at all. Off and still off records nothing new.
  const mustSave = editing && (form.enabled || savedOn || firstTime);
  // Settings: the save bar shows only for a real change.
  const dirty =
    !!stored &&
    editing &&
    (form.enabled !== stored.enabled ||
      (form.enabled && (reconsent || form.packageId !== stored.packageId || thresholdSeconds !== stored.triggerSeconds)));
  const saveBar = {
    dirty,
    message: blocked ?? t('ui.unsavedChanges'),
    saveDisabled: !!blocked,
    saving: busy,
    onSave: () => {
      if (blocked || busy) return;
      persist().catch(failed);
    },
    onDiscard: () => reset(openForm),
  };

  const next = {
    label: editing && form.enabled ? t('plan.auto.saveContinue') : t('wizard.continue'),
    disabled: !!blocked,
    loading: busy,
    hint: blocked ?? undefined,
    onClick: () => {
      if (!active || !mustSave) return onDone();
      if (blocked || busy) return;
      persist().then(onDone, failed);
    },
  };

  return {
    mode,
    reconsent,
    ready: active && !!ov,
    /** The preview's switch, before subscribing. */
    preference,
    setPreference: (on: boolean) => {
      writeChoice(business.id, on);
      setPreference(on);
    },
    ov,
    form,
    editing,
    stored,
    price,
    pkg,
    busy,
    available,
    thresholdSeconds,
    thresholdError: thresholdTouched && thresholdBad ? t('prepaid.auto.err.threshold_invalid') : undefined,
    next,
    saveBar,
    setEnabled: (on: boolean) => setForm((f) => ({ ...f, enabled: on, accepted: false })),
    setPackage: (packageId: string) => {
      // Propose the package's own threshold (30/60/100/200 min), unless the
      // owner typed one. Back on the saved package, the saved threshold.
      const suggested =
        stored?.enabled && packageId === stored.packageId
          ? stored.triggerSeconds
          : findPackage(PREPAID_CATALOGUE, packageId, ov?.currency ?? business.currency)?.defaultTriggerSeconds;
      setForm((f) => ({
        ...f,
        packageId,
        thresholdMin: !thresholdTyped && suggested ? String(Math.round(suggested / 60)) : f.thresholdMin,
        accepted: false,
      }));
    },
    setThreshold: (v: string) => {
      setThresholdTyped(true);
      setForm((f) => ({ ...f, thresholdMin: v, accepted: false }));
    },
    blurThreshold: () => setThresholdTouched(true),
    setAccepted: (accepted: boolean) => setForm((f) => ({ ...f, accepted })),
    edit: () => reset(true),
    cancelEdit: () => reset(openForm),
  };
}

export type AutoTopupControl = ReturnType<typeof useAutoTopup>;

/** Phone with a slash — the template's callout icon (decorative). */
function PhoneOffIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
      <path d="M2 2l20 20" />
    </svg>
  );
}

/**
 * The warning about running out of minutes. Static content, not a live
 * region; the icon is decorative and the headline carries the meaning.
 * Red while auto top-up is OFF (the state the warning exists for), orange
 * while it is on but not yet saved, or locked on before subscribing.
 */
export function AutoTopupWarning({ variant, off }: { variant: 'off' | 'locked'; off?: boolean }) {
  const { t } = useI18n();
  return (
    <div className={`plan-warn${off ? ' plan-warn--off' : ''}`}>
      <span className="plan-warn__icon">
        <PhoneOffIcon size={20} />
      </span>
      <p className="plan-warn__head">{t('plan.auto.warn.head')}</p>
      <p className="plan-warn__body">{variant === 'locked' ? t('plan.auto.warn.locked') : t('plan.auto.warn.off')}</p>
    </div>
  );
}

function Heading() {
  const { t } = useI18n();
  return (
    <>
      <h2 id="plan-auto-h" className="plan-h2">{t('plan.auto.subtitle')}</h2>
      <p className="plan-auto__hint">{t('plan.auto.hint')}</p>
    </>
  );
}

/**
 * The "Automatic top-up" panel's first line: title, the optional note as its
 * description, and the on/off switch the title names.
 */
function PanelHead({ checked, onChange, disabled, mode = 'onboarding' }: { checked: boolean; onChange?: (on: boolean) => void; disabled?: boolean; mode?: AutoTopupMode }) {
  const { t } = useI18n();
  return (
    <div className="plan-auto__card-head">
      <div className="plan-auto__card-text">
        <h3 id="plan-auto-sw" className="plan-auto__card-title">{t('prepaid.auto.title')}</h3>
        <p id="plan-auto-sw-desc" className="plan-auto__card-desc">{mode === 'settings' ? t('plan.auto.optionalSettings') : t('plan.auto.optional')}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        readOnly={!onChange}
        onChange={(e) => onChange?.(e.target.checked)}
        aria-labelledby="plan-auto-sw"
        aria-describedby="plan-auto-sw-desc"
      />
    </div>
  );
}

/**
 * Before the subscription. In the wizard (`ctl`) the switch is live and
 * starts ON, so the owner can switch it off before paying; the choice is the
 * form's starting point afterwards. Elsewhere (Settings, before
 * re-subscribing) it is a read-only preview.
 */
function LockedAutoTopup({ ctl }: { ctl?: AutoTopupControl }) {
  const { t } = useI18n();
  const live = ctl?.mode === 'onboarding' ? ctl : undefined;
  const on = live ? live.preference : true;
  return (
    <section className="plan-auto" aria-labelledby="plan-auto-h">
      <Heading />
      <AutoTopupWarning variant={on ? 'locked' : 'off'} off={!on} />
      <div className="plan-auto__card">
        <PanelHead checked={on} onChange={live?.setPreference} disabled={!live} />
      </div>
      <p className="plan-auto__foot">{t('plan.auto.locked')}</p>
    </section>
  );
}

/**
 * The saved card automatic top-ups are charged to: the one from the
 * subscription payment. Adding or replacing it is Stripe's hosted page; it
 * returns to the Activate step (or Settings › Billing), which confirms it.
 */
function PaymentCardBox({ card, businessId }: { card: Overview['card']; businessId: string }) {
  const { t } = useI18n();
  const setup = useStartCardSetup(businessId);
  return (
    <div className={`plan-auto__payment${card ? '' : ' plan-auto__payment--missing'}`}>
      <span className="plan-auto__payment-text">
        {card ? (
          <>
            <span className="plan-auto__payment-title">{`${card.brand.toUpperCase()} •••• ${card.last4}`}</span>
            <span className="plan-auto__payment-sub">
              {t('plan.auto.cardExpires', { exp: `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}` })}
            </span>
          </>
        ) : (
          <>
            <span className="plan-auto__payment-title">{t('plan.auto.noCardTitle')}</span>
            <span className="plan-auto__payment-sub">{t('plan.auto.noCardBody')}</span>
          </>
        )}
        {setup.isError && <span className="plan-auto__help plan-auto__help--error" role="alert">{billingError(t, setup.error)}</span>}
      </span>
      <Button
        variant={card ? 'secondary' : 'primary'}
        className={card ? 'plan-auto__outline' : undefined}
        loading={setup.isPending}
        disabled={setup.isPending}
        onClick={() => setup.mutate()}
      >
        {card ? t('prepaid.auto.replaceCard') : t('prepaid.auto.addCard')}
      </Button>
    </div>
  );
}

/**
 * While the billing data loads: the heading and hint are static, so they
 * show at once; the warning and the form are skeletons until the settings
 * (saved choice, card, prices) are known.
 */
function AutoTopupLoading() {
  const { t } = useI18n();
  return (
    <section className="plan-auto" aria-labelledby="plan-auto-h" aria-busy="true">
      <Heading />
      <div className="plan-auto__skeleton">
        <Skeleton height={96} radius="var(--tc-radius-md)" />
        <div className="plan-auto__card" role="status" aria-label={t('plan.auto.loading')}>
          <div className="plan-auto__card-head">
            <div className="plan-auto__card-text">
              <Skeleton height={20} width={160} />
              <Skeleton height={16} width="60%" />
            </div>
            <Skeleton height={28} width={48} radius={999} />
          </div>
          <Skeleton height={64} radius="var(--tc-radius-md)" />
          <div className="plan-auto__fields">
            <Skeleton height={16} width={90} />
            <Skeleton height={44} radius="var(--tc-radius-sm)" />
            <Skeleton height={16} width={200} />
            <Skeleton height={44} radius="var(--tc-radius-sm)" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The auto top-up widget, wherever it appears. Before the subscription: the
 * preview (live switch in the wizard, read-only elsewhere). After it: a
 * skeleton while loading, then the status row (saved on) or the form.
 */
export function AutoTopupWidget({
  ctl,
  business,
  subscribed,
  loading = false,
}: {
  ctl?: AutoTopupControl;
  business: BusinessDoc;
  subscribed: boolean;
  loading?: boolean;
}) {
  if (!subscribed) return <LockedAutoTopup ctl={ctl} />;
  if (!ctl) return null;
  if (loading || !ctl.ready || !ctl.ov || !ctl.stored) return <AutoTopupLoading />;
  return <AutoTopupForm ctl={ctl} business={business} />;
}

function AutoTopupForm({ ctl, business }: { ctl: AutoTopupControl; business: BusinessDoc }) {
  const { t, tag } = useI18n();
  const { ov, form, editing, stored, price, pkg } = ctl;
  if (!ov || !stored) return null;

  const savedOn = !editing && stored.enabled;
  const minutes = (s: number) => Math.round(s / 60);
  const willChargeNow = editing && form.enabled && !!price && !!pkg && ctl.thresholdSeconds > 0 && ctl.available <= ctl.thresholdSeconds;
  const card = ov.card ? `${ov.card.brand.toUpperCase()} •••• ${ov.card.last4}` : null;

  return (
    <section className="plan-auto" aria-labelledby="plan-auto-h">
      <Heading />

      {savedOn ? (
        <div className="plan-auto__status">
          <span className="plan-auto__status-icon" aria-hidden>
            <Icon name="check" size={14} strokeWidth={3} />
          </span>
          <div className="plan-auto__status-text">
            <p className="plan-auto__status-head">
              {t('plan.auto.active', {
                minutes: minutes(findPackageSeconds(ov, stored.packageId)),
                threshold: minutes(stored.triggerSeconds),
              })}
            </p>
            <p className="plan-auto__status-sub">
              {card ? `${t('plan.auto.chargedTo', { card })} ` : ''}
              {t('plan.auto.caveat')}
            </p>
          </div>
          <Button variant="secondary" className="plan-auto__outline" onClick={() => ctl.edit()}>{t('plan.auto.edit')}</Button>
        </div>
      ) : (
        <AutoTopupWarning variant="off" off={!form.enabled} />
      )}

      {editing && (
        <>
          <div className="plan-auto__card">
            <PanelHead checked={form.enabled} onChange={ctl.setEnabled} mode={ctl.mode} />
            {form.enabled && <PaymentCardBox card={ov.card} businessId={business.id} />}

            {form.enabled && (
              <>
                <div className="plan-auto__fields">
                  <label className="plan-auto__field">
                    <span className="plan-auto__label">{t('prepaid.auto.package')}</span>
                    <Select
                      value={form.packageId}
                      onChange={(e) => ctl.setPackage(e.target.value)}
                      options={ov.packages.map((p) => ({
                        value: p.packageId,
                        label:
                          p.unitAmountMinor !== null
                            ? t('plan.auto.packageOption', { minutes: minutes(p.seconds), price: formatMoney(p.unitAmountMinor, p.currency, tag) })
                            : t('prepaid.topup.package', { minutes: minutes(p.seconds) }),
                      }))}
                    />
                  </label>
                  <label className="plan-auto__field">
                    <span className="plan-auto__label">{t('prepaid.auto.threshold')}</span>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      value={form.thresholdMin}
                      invalid={!!ctl.thresholdError}
                      aria-invalid={!!ctl.thresholdError}
                      aria-describedby="plan-auto-thr-hint"
                      onChange={(e) => ctl.setThreshold(e.target.value)}
                      onBlur={ctl.blurThreshold}
                    />
                    <span id="plan-auto-thr-hint" className={`plan-auto__help${ctl.thresholdError ? ' plan-auto__help--error' : ''}`}>
                      {ctl.thresholdError ?? t('prepaid.auto.thresholdHint')}
                    </span>
                  </label>
                </div>
                {/* Nothing to authorise without a saved card. */}
                {price && ov.card && (
                  <div className="plan-auto__consent">
                    <Checkbox
                      checked={form.accepted}
                      onChange={(e) => ctl.setAccepted(e.target.checked)}
                      label={t('plan.auto.consent', { price, business: business.name })}
                    />
                  </div>
                )}
                {/* Saving queues the first purchase when the balance is already
                    at the threshold (included minutes count): said BEFORE the
                    owner saves, and announced when it appears. */}
                <div aria-live="polite">
                  {willChargeNow && (
                    <p className="plan-auto__charge">
                      <Icon name="info" size={16} />
                      <span>{t('plan.auto.willCharge', { minutes: minutes(ctl.available), price: price! })}</span>
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Back to the status row — only when Edit opened the form. */}
            {stored.enabled && !ctl.reconsent && (
              <button type="button" className="plan-auto__link" onClick={ctl.cancelEdit}>
                {t('common.cancel')}
              </button>
            )}
          </div>
        </>
      )}

      {/* Settings: the portal's usual save bar. The wizard saves from its
          own Continue instead. */}
      {ctl.mode === 'settings' && ctl.saveBar.dirty && (
        <SaveBar
          saveLabel={t('common.saveChanges')}
          discardLabel={t('common.discard')}
          regionLabel={t('ui.unsavedChanges')}
          message={ctl.saveBar.message}
          dirty
          saveDisabled={ctl.saveBar.saveDisabled}
          saving={ctl.saveBar.saving}
          onDiscard={ctl.saveBar.onDiscard}
          onSave={ctl.saveBar.onSave}
        />
      )}
    </section>
  );
}

function findPackageSeconds(ov: Overview, packageId: string | null): number {
  return ov.packages.find((p) => p.packageId === packageId)?.seconds ?? 0;
}
